import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SUPABASE_BUCKET_NAME } from '../config/supabase.config';
import { ArrowLeft, ArrowRight, Edit, Loader } from 'lucide-react'; // Add Loader import
import { safeConsoleLog } from '../utils/maskLog';
import Footer from '../components/Footer';
import { getJakartaISOString } from '../utils/dateUtils'; // Add getJakartaISOString import
import StoryBlobs from '../components/StoryBlobs';
import { useTheme } from '../context/ThemeContext';

// Removed duplicate safeConsoleLog declaration because it's imported from '../utils/maskLog'

interface LinkItem {
  url: string;
  title: string;
}

export default function StoryDetails() {
  const { maskedId } = useParams<{ maskedId: string }>();
  const [story, setStory] = useState<any>(null);
  const [error, setError] = useState('');
  const [pictureUrl, setPictureUrl] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<{ view_count: number; cities: Record<string, number>; ips?: Record<string, any> }>({ view_count: 0, cities: {} });
  const fetched = useRef(false); // Flag to prevent duplicate fetch
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [editedMadeOn, setEditedMadeOn] = useState('');
  const isAdmin = localStorage.getItem('admin');
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [adjacentStories, setAdjacentStories] = useState<{
    previous?: { id: string; title: string };
    next?: { id: string; title: string };
  }>({});
  // Detect dark mode (same as Interests)
  const { dark } = useTheme();

  const navigate = useNavigate();

  // Add this helper function to format the content
  const formatContent = (content: string) => {
    return content.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  // Add this helper function to get a friendly website name
  const getWebsiteName = (url: string) => {
    try {
      const hostname = new URL(url).hostname;
      // Remove common prefixes and suffixes and format nicely
      const name = hostname
        .replace(/^www\./, '')
        .replace(/\.(com|org|net|io|dev|co|id)$/, '')
        .split('.')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
      return name;
    } catch {
      return 'Website';
    }
  };

  const updateAnalyticsAndTimestamp = async (storyId: string, updates: any) => {
    try {
      // Ensure we have valid IP data
      if (!updates.ipData?.ip || !updates.ipData?.city) {
        console.error('Invalid IP data:', updates.ipData);
        return;
      }

      // Get current story data first to ensure accurate updates
      const { data: currentStory, error: fetchError } = await supabase
        .from('stories')
        .select('view_count, view_cities, view_ips')
        .eq('id', storyId)
        .single();

      if (fetchError) {
        console.error('Error fetching current story:', fetchError);
        return;
      }

      // Prepare the analytics update with proper data structure
      const analyticsUpdate = {
        view_count: (currentStory.view_count || 0) + 1,
        view_cities: {
          ...(currentStory.view_cities || {}),
          [updates.ipData.city]: ((currentStory.view_cities || {})[updates.ipData.city] || 0) + 1
        },
        view_ips: {
          ...(currentStory.view_ips || {}),
          [updates.ipData.ip]: {
            ip: updates.ipData.ip,
            count: ((currentStory.view_ips || {})[updates.ipData.ip]?.count || 0) + 1,
            last_visit: getJakartaISOString(),
            cities: [...new Set([
              ...((currentStory.view_ips || {})[updates.ipData.ip]?.cities || []),
              updates.ipData.city
            ])]
          }
        },
        lastupdatetime: getJakartaISOString()
      };

      const { data, error } = await supabase
        .from('stories')
        .update(analyticsUpdate)
        .eq('id', storyId)
        .select('view_count, view_cities, view_ips, lastupdatetime')
        .single();

      if (error) {
        console.error('Error updating analytics:', error);
        throw error;
      }

      // Update local state if successful
      if (isAdmin && data) {
        setAnalytics({
          view_count: data.view_count || 0,
          cities: data.view_cities || {},
          ips: data.view_ips || {}
        });
      } else {
        setAnalytics({ view_count: 0, cities: {} }); // public tidak dapat analytics
      }
    } catch (err) {
      console.error('Failed to update analytics:', err);
    }
  };

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    const fetchStory = async () => {
      try {
        if (!maskedId) {
          setError('Invalid story ID');
          return;
        }
        const id = atob(maskedId); // Decode the ID
        if (isAdmin) {
          // Admin: fetch langsung ke Supabase, semua kolom
          let selectFields = '*';
          const { data, error } = await supabase
            .from('stories')
            .select(selectFields)
            .eq('id', id)
            .single();
          if (error) {
            setError('Failed to fetch story details');
            console.error('Error fetching story:', error);
          } else {
            setStory(data);
            // Type guard: hanya akses picture jika data bertipe object dan punya property picture
            if (data && typeof data === 'object' && (data as { picture?: string }).picture) {
              const picture = (data as { picture: string }).picture;
              setIsImageLoading(true);
              safeConsoleLog('Raw picture path:', picture);
              let filePath = picture.replace(/^\/+/, '').replace(/\/{2,}/g, '');
              // Remove any encoded "\x" patterns and leading "x"
              filePath = filePath.replace(/^\\?x/, '');
              // Helper to convert hex string to ASCII
              const hexToAscii = (hex: string): string => {
                let str = '';
                for (let i = 0; i < hex.length; i += 2) {
                  str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
                }
                return str;
              };
              // If filePath is all hexadecimal, decode it
              if (/^[0-9a-f]+$/i.test(filePath)) {
                filePath = hexToAscii(filePath);
                safeConsoleLog('Decoded file path:', filePath);
              }
              const { data: forcedSignedData, error: forcedSignedError } = await supabase
                .storage
                .from(SUPABASE_BUCKET_NAME)
                .createSignedUrl(filePath, 3600);
              if (forcedSignedError) {
                console.error('Error creating forced signed URL:', forcedSignedError);
              } else if (forcedSignedData?.signedUrl) {
                setPictureUrl(forcedSignedData.signedUrl);
                safeConsoleLog('Forced Signed URL:', forcedSignedData.signedUrl);
              }
              setIsImageLoading(false);
            }
            setIsImageLoading(false);
          }
        } else {
          // Public: fetch via Netlify Function, fallback ke Supabase jika gagal
          let storyData = null;
          try {
            const res = await fetch(`/api/getStories?id=eq.${id}&limit=1`);
            if (!res.ok) throw new Error('Failed to fetch story');
            // Check if response is JSON, otherwise fallback
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              throw new Error('Not a JSON response');
            }
            const stories = await res.json();
            storyData = stories && stories[0] ? stories[0] : null;
          } catch (e) {
            // Fallback ke Supabase langsung (local dev)
            try {
              const { data, error } = await supabase
                .from('stories')
                .select('id, title, content, made_on, url_links, picture')
                .eq('id', id)
                .single();
              if (error) throw error;
              storyData = data;
            } catch (err) {
              setError('Failed to fetch story details');
              console.error('Error fetching story (fallback):', err);
              setIsImageLoading(false);
              return;
            }
          }
          setStory(storyData);
          // Type guard: hanya akses picture jika data bertipe object dan punya property picture
          if (storyData && typeof storyData === 'object' && (storyData as { picture?: string }).picture) {
            const picture = (storyData as { picture: string }).picture;
            setIsImageLoading(true);
            safeConsoleLog('Raw picture path:', picture);
            let filePath = picture.replace(/^\/+/, '').replace(/\/{2,}/g, '');
            // Remove any encoded "\x" patterns and leading "x"
            filePath = filePath.replace(/^\\?x/, '');
            // Helper to convert hex string to ASCII
            const hexToAscii = (hex: string): string => {
              let str = '';
              for (let i = 0; i < hex.length; i += 2) {
                str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
              }
              return str;
            };
            // If filePath is all hexadecimal, decode it
            if (/^[0-9a-f]+$/i.test(filePath)) {
              filePath = hexToAscii(filePath);
              safeConsoleLog('Decoded file path:', filePath);
            }
            const { data: forcedSignedData, error: forcedSignedError } = await supabase
              .storage
              .from(SUPABASE_BUCKET_NAME)
              .createSignedUrl(filePath, 3600);
            if (forcedSignedError) {
              console.error('Error creating forced signed URL:', forcedSignedError);
            } else if (forcedSignedData?.signedUrl) {
              setPictureUrl(forcedSignedData.signedUrl);
              safeConsoleLog('Forced Signed URL:', forcedSignedData.signedUrl);
            }
            setIsImageLoading(false);
          }
          setIsImageLoading(false);
        }
      } catch (err) {
        setIsImageLoading(false);
        setError('Invalid story ID');
        console.error('Error decoding story ID:', err);
      }
    };

    const fetchStoryAndUpdateAnalytics = async () => {
      if (!maskedId) {
        setError('Invalid story ID');
        return;
      }
      
      try {
        const decodedId = atob(maskedId);
        let selectFields = 'id, title, content, made_on, url_links, picture';
        if (isAdmin) {
          selectFields = '*';
        }
        const { data: currentStory, error: fetchError } = await supabase
          .from('stories')
          .select(selectFields)
          .eq('id', decodedId)
          .single();

        if (fetchError || !currentStory) {
          setError('Failed to fetch story');
          return;
        }
        
        setStory(currentStory);

        // Get IP data with retry mechanism
        let ipData;
        try {
          const response = await fetch('https://ipapi.co/json/');
          ipData = await response.json();
          
          // Validate IP data
          if (!ipData.ip || !ipData.city) {
            throw new Error('Invalid IP data received');
          }
        } catch (e) {
          console.error('IP lookup error:', e);
          // Fallback to request IP if available, otherwise use unknown
          ipData = { 
            city: 'unknown',
            ip: 'unknown'
          };
        }

        await updateAnalyticsAndTimestamp(decodedId, { ipData });

      } catch (err) {
        console.error('Error in fetchStoryAndUpdateAnalytics:', err);
        setError('Failed to update analytics');
      }
    };

    const fetchAdjacentStories = async (currentId: string) => {
      // Fetch all stories ordered by made_on to determine next/previous
      const { data: allStories } = await supabase
        .from('stories')
        .select('id, title, made_on')
        .order('made_on', { ascending: false });

      if (allStories) {
        const currentIndex = allStories.findIndex(s => s.id === currentId);
        if (currentIndex > -1) {
          setAdjacentStories({
            previous: currentIndex > 0 ? allStories[currentIndex - 1] : undefined,
            next: currentIndex < allStories.length - 1 ? allStories[currentIndex + 1] : undefined,
          });
        }
      }
    };

    if (maskedId) {
      const id = atob(maskedId);
      fetchAdjacentStories(id);
    }

    fetchStory();
    fetchStoryAndUpdateAnalytics();
  }, [maskedId]);

  const handleEditSubmit = async () => {
    try {
      if (!isAdmin) {
        setError('Unauthorized to edit story');
        return;
      }

      // For admin updates, include all fields
      const id = atob(maskedId!);
      const { error: updateError } = await supabase
        .from('stories')
        .update({
          title: editedTitle,
          content: editedContent,
          made_on: editedMadeOn || null,
          lastupdatetime: getJakartaISOString() // Use Jakarta timezone
        })
        .eq('id', id);

      if (updateError) throw updateError;
      
      setStory({ 
        ...story, 
        title: editedTitle,
        content: editedContent,
        made_on: editedMadeOn 
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating story:', err);
      setError('Failed to update story');
    }
  };

  // When starting edit mode, populate all fields
  const startEditing = () => {
    setIsEditing(true);
    setEditedTitle(story.title || '');
    setEditedContent(story.content || '');
    setEditedMadeOn(story.made_on || '');
  };

  const handleNavigateToStory = (newStoryId: string) => {
    // Reset states
    setStory(null);
    setPictureUrl(null);
    setIsImageLoading(true);
    setAnalytics({ view_count: 0, cities: {} });
    fetched.current = false;

    // Navigate to new story
    const newMaskedId = btoa(newStoryId);
    navigate(`/story/${newMaskedId}`);
  };

  // Update the renderLinks function
  const renderLinks = () => {
    if (story.url_links && Array.isArray(story.url_links)) {
      const validLinks = story.url_links.filter((link: LinkItem) => link.url.trim());
      
      if (validLinks.length > 0) {
        return (
          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-2">Tautan yang tersedia</h3>
            <div className="space-y-2">
              {validLinks.map((link: LinkItem, index: number) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors group"
                >
                  <span>
                    {link.title && `${link.title} - `}{getWebsiteName(link.url)}
                  </span>
                  <svg 
                    className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                    />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        );
      }
    }
    return null;
  };

  if (error) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center bg-transparent">
        <div className="flex flex-col items-center justify-center gap-4 mt-20">
          <Loader className="animate-spin text-[#b4befe]" size={48} />
          <p className="text-red-500 text-center text-lg font-semibold">{error || 'Something went wrong, please try again later.'}</p>
          <Link to="/story" className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">Back to Stories</Link>
        </div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center bg-transparent">
        <Loader className="animate-spin text-[#b4befe]" size={48} />
        <p className="text-gray-400 text-center mt-6">Loading story details...</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-start pt-16 pb-10 bg-transparent">
      <StoryBlobs dark={dark} />
      <div className="w-full max-w-2xl mx-auto px-2 sm:px-6">
        <Link
          to="/story"
          className="inline-flex items-center gap-2 mb-8 transition-colors text-lg
            text-[#232634] dark:text-[#f9e2af] font-bold
            px-3 py-1 rounded-lg bg-white/40 dark:bg-[#232634]/60 shadow-sm backdrop-blur-sm
            border border-[#e0d7d2] dark:border-[#45475a] sm:static top-4 left-2 z-20
            focus:outline-none focus:ring-2 focus:ring-[#b4befe] focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#181825]"
          style={{ maxWidth: 'fit-content' }}
        >
          <ArrowLeft size={20} />
          <span>Back to Stories</span>
        </Link>
        {/* Card container */}
        <div className="rounded-3xl bg-white/90 dark:bg-[#232634]/90 shadow-xl border border-[#e0d7d2] dark:border-[#45475a] px-6 py-8 md:px-10 md:py-10 flex flex-col gap-4 items-start max-w-2xl mx-auto relative overflow-visible" style={{backdropFilter:'blur(6px)'}}>
          {/* Border glow using a pseudo-element */}
          <span
            className="pointer-events-none absolute inset-0 rounded-3xl z-0"
            style={{
              boxShadow: dark
                ? '0 0 0 6px #f9e2af33, 0 0 24px 8px #f9e2af22'
                : '0 0 0 6px #f2cdcd33, 0 0 24px 8px #f2cdcd22',
              opacity: 0.85,
              transition: 'box-shadow 0.3s',
            }}
          />
          {story && (
            <>
              <h1 className="text-3xl md:text-4xl font-extrabold mb-2 font-display text-[#232634] dark:text-white">{story.title}</h1>
              <div className="text-base text-[#6c6f85] dark:text-[#a6adc8] mb-4">{story.made_on && new Date(story.made_on).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              <div className="text-lg text-[#232634] dark:text-[#f9e2af] mb-4 whitespace-pre-line leading-relaxed min-h-[48px] flex items-center justify-center">
                {story.content && story.content.trim() ? (
                  story.content
                ) : (
                  <div className="w-full flex flex-col items-center justify-center py-8">
                    <span className="block w-12 h-1 bg-[#fab387] dark:bg-[#b4befe] rounded-full mb-4 opacity-60" />
                    <span className="italic text-center text-base md:text-lg text-[#a6adc8] dark:text-[#fab387]">
                      Sorry, the content you're looking for might have been deleted or archived.
                    </span>
                  </div>
                )}
              </div>
              {story.picture && (
                <div className="relative mt-2 flex justify-center w-full">
                  {/* Playful, theme-adaptive blob behind image */}
                  {dark ? (
                    <svg className="absolute -top-8 left-1/2 -translate-x-1/2 w-64 h-32 md:w-96 md:h-40 opacity-40 z-0 animate-blob-float" viewBox="0 0 400 160" fill="none"><ellipse cx="200" cy="80" rx="180" ry="60" fill="#b4befe" /></svg>
                  ) : (
                    <svg className="absolute -top-8 left-1/2 -translate-x-1/2 w-64 h-32 md:w-96 md:h-40 opacity-40 z-0 animate-blob-float" viewBox="0 0 400 160" fill="none"><ellipse cx="200" cy="80" rx="180" ry="60" fill="#f9e2af" /></svg>
                  )}
                  {/* Centered image loading state inside border */}
                  <div className="w-full max-w-lg aspect-[4/3] flex items-center justify-center">
                    {isImageLoading && (
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center z-10 w-full max-w-lg h-full">
                        <span className="text-5xl animate-bounce mb-2">{dark ? '🌙' : '☀️'}</span>
                        <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-[#b4befe]/30 to-[#f9e2af]/30 dark:from-[#b4befe]/40 dark:to-[#a6e3a1]/30 animate-pulse flex items-center justify-center relative overflow-hidden">
                          <div className="absolute inset-0 animate-shine rounded-2xl" style={{background: 'linear-gradient(90deg, #fff0 0%, #fff8 50%, #fff0 100%)', backgroundSize: '200px 100%'}} />
                          <span className="text-3xl opacity-60 animate-spin-slow">🖼️</span>
                        </div>
                        <span className="text-[#a6adc8] dark:text-[#fab387] animate-pulse font-semibold text-sm mt-2">Loading image...</span>
                      </div>
                    )}
                    {/* Clickable image for preview */}
                    <a
                      href={pictureUrl ? `/preview/${story.id}` : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full max-w-lg relative z-10"
                      onClick={e => {
                        if (!pictureUrl) e.preventDefault();
                      }}
                      style={{ cursor: pictureUrl ? 'zoom-in' : 'not-allowed' }}
                    >
                      <img
                        src={pictureUrl ?? undefined}
                        alt={story.title}
                        className={`w-full max-w-lg h-auto object-cover rounded-2xl shadow-lg border-4 border-white/30 transition-all ${isImageLoading ? 'opacity-40 blur-sm grayscale' : 'opacity-95'}`}
                        onLoad={() => setIsImageLoading(false)}
                        onError={() => setIsImageLoading(false)}
                        style={{ pointerEvents: isImageLoading ? 'none' : 'auto' }}
                      />
                    </a>
                  </div>
                </div>
              )}
              {/* Admin-only analytics UI */}
              {isAdmin && analytics && (
                <div className="w-full bg-[#f9e2af]/30 dark:bg-[#b4befe]/10 border border-[#f9e2af] dark:border-[#b4befe] rounded-xl px-4 py-3 mb-4 mt-2 text-[#232634] dark:text-[#f9e2af] shadow-sm">
                  <div className="font-bold text-lg mb-1 flex items-center gap-2">
                    <span>Analytics</span>
                    {/*<span className="text-xs font-normal bg-[#f9e2af]/60 dark:bg-[#b4befe]/30 text-[#232634] dark:text-[#b4befe] px-2 py-0.5 rounded"></span>*/}
                  </div>
                  <div className="flex flex-wrap gap-4 items-center mb-2">
                    <div className="text-base font-semibold">👀 Views: <span className="font-mono">{analytics.view_count}</span></div>
                    <div className="text-base font-semibold">🏙️ Cities: <span className="font-mono">{Object.keys(analytics.cities || {}).length}</span></div>
                    {analytics.ips && (
                      <div className="text-base font-semibold">📍 IPs: <span className="font-mono">{Object.keys(analytics.ips).length}</span></div>
                    )}
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-semibold">City breakdown:</span>
                    <ul className="ml-4 list-disc">
                      {Object.entries(analytics.cities || {}).map(([city, count]) => (
                        <li key={city}>{city}: <span className="font-mono">{count}</span></li>
                      ))}
                    </ul>
                  </div>
                  {analytics.ips && (
                    <div className="text-sm mb-1">
                      <span className="font-semibold">IP breakdown:</span>
                      <ul className="ml-4 list-disc">
                        {Object.entries(analytics.ips)
                          .sort((a: any, b: any) => {
                            // Sort descending by last_visit datetime
                            const dateA = new Date(a[1].last_visit).getTime();
                            const dateB = new Date(b[1].last_visit).getTime();
                            return dateB - dateA;
                          })
                          .map(([ip, info]: any) => (
                            <li key={ip}>
                              <span className="font-mono">{ip}</span> — <span className="font-mono">{info.count}</span> views, last: {info.last_visit}, cities: {Array.isArray(info.cities) ? info.cities.join(', ') : ''}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                  <div className="text-xs text-[#6c6f85] dark:text-[#a6adc8] mt-1">Last update: {story.lastupdatetime ? new Date(story.lastupdatetime).toLocaleString() : '-'}</div>
                </div>
              )}
              {/* Show links if available */}
              {Array.isArray(story.url_links) && story.url_links.length > 0 && (
                <div className="mt-6 w-full">
                  <h3 className="text-xl font-semibold mb-2 text-[#232634] dark:text-[#f9e2af]">Tautan yang tersedia</h3>
                  <div className="space-y-2">
                    {story.url_links.filter((l:any) => l.url && l.url.trim()).map((link:any, idx:number) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-500 hover:text-blue-400 transition-colors group font-semibold"
                      >
                        <span>{link.title ? `${link.title} - ` : ''}{(() => { try { return new URL(link.url).hostname.replace(/^www\./, ''); } catch { return link.url; } })()}</span>
                        <svg className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {/* ...existing navigation for previous/next, Footer, etc... */}
        <div className="max-w-4xl mx-auto mt-8 mb-8 flex justify-between items-center gap-4">
          {adjacentStories.previous && (
            <button
              onClick={() => handleNavigateToStory(adjacentStories.previous!.id)}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group flex-1"
            >
              <ArrowLeft className="transition-transform group-hover:-translate-x-2" />
              <div className="overflow-hidden">
                <div className="text-sm text-gray-500">Previous</div>
                <div className="truncate">{adjacentStories.previous.title}</div>
              </div>
            </button>
          )}
          {adjacentStories.next && (
            <button
              onClick={() => handleNavigateToStory(adjacentStories.next!.id)}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group flex-1 justify-end text-right"
            >
              <div className="overflow-hidden">
                <div className="text-sm text-gray-500">Next</div>
                <div className="truncate">{adjacentStories.next.title}</div>
              </div>
              <ArrowRight className="transition-transform group-hover:translate-x-2" />
            </button>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
}
